import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import WaveformPlayer from "./WaveformPlayer";
import VideoCard from "./VideoCard";
import { ExternalLink } from "lucide-react";
import {
  Filter,
  LayoutGrid,
  Tv,
  Radio,
  Mic2,
  Music2,
  Smartphone,
  Waves,
  Volume2,
  Play,
} from "lucide-react";

// Icons for paragraphs (static fallback)
const paragraphIcons = [
  <span key="check" className="text-[var(--primary-color)]">
    ✔️
  </span>,
  <span key="arrow" className="text-[var(--primary-color)]">
    ➡️
  </span>,
  <span key="target" className="text-[var(--primary-color)]">
    🎯
  </span>,
  <span key="message" className="text-[var(--primary-color)]">
    💬
  </span>,
  <span key="zap" className="text-[var(--primary-color)]">
    ⚡
  </span>,
  <span key="chart" className="text-[var(--primary-color)]">
    📊
  </span>,
  <span key="pen" className="text-[var(--primary-color)]">
    ✏️
  </span>,
  <span key="lightbulb" className="text-[var(--primary-color)]">
    💡
  </span>,
];

// Base categories that are always available
const BASE_CATEGORIES = [
  { id: "all", label: "All Content", icon: LayoutGrid },
  { id: "video", label: "Video", icon: Tv },
  { id: "sound", label: "Sound", icon: Volume2 },
];

// Helper function to generate categories from sections
const generateCategoriesFromSections = (sections) => {
  const customCategories = new Map();

  sections.forEach((section) => {
    const label = section.categoryLabel?.trim();
    if (label && label !== "Video" && label !== "Audio" && label !== "Sound") {
      if (!customCategories.has(label)) {
        customCategories.set(label, {
          label,
          hasVideo: false,
          hasAudio: false,
          sections: [],
        });
      }

      const category = customCategories.get(label);
      category.sections.push(section);

      if (section.mediaType === "video") {
        category.hasVideo = true;
      } else if (section.mediaType === "audio") {
        category.hasAudio = true;
      }
    }
  });

  // Convert to array and create category objects with appropriate icons
  return Array.from(customCategories.values()).map((category) => {
    const { label, hasVideo, hasAudio } = category;

    // Choose icon based on media type content
    let icon = Radio; // default
    const lowerLabel = label.toLowerCase();

    // Priority: Check actual media types first
    if (hasVideo && hasAudio) {
      icon = Play; // Mixed content
    } else if (hasVideo) {
      icon = Tv; // Video content
    } else if (hasAudio) {
      icon = Volume2; // Audio content
    } else {
      // Fallback to label-based detection
      if (lowerLabel.includes("music") || lowerLabel.includes("song"))
        icon = Music2;
      else if (lowerLabel.includes("voice") || lowerLabel.includes("narration"))
        icon = Mic2;
      else if (lowerLabel.includes("commercial") || lowerLabel.includes("ad"))
        icon = Play;
      else if (lowerLabel.includes("podcast")) icon = Waves;
      else if (
        lowerLabel.includes("app") ||
        lowerLabel.includes("ui") ||
        lowerLabel.includes("interface")
      )
        icon = Smartphone;
      else if (lowerLabel.includes("video") || lowerLabel.includes("film"))
        icon = Tv;
      else if (lowerLabel.includes("audio") || lowerLabel.includes("sound"))
        icon = Volume2;
    }

    return { id: label.toLowerCase().replace(/\s+/g, "-"), label, icon };
  });
};

const DynamicDeliverySection = ({ sections = [], optimizedContent = {}, deliveryDriveUrl = "" }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [playingVideos, setPlayingVideos] = useState({});

  // Handle video play/pause
  const handleVideoToggle = (sectionIndex) => {
    setPlayingVideos((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  // Generate dynamic categories from sections
  const customCategories = generateCategoriesFromSections(sections);
  const allCategories = [...BASE_CATEGORIES, ...customCategories];

  // Filter sections based on selected category
  const filteredSections = sections.filter((section) => {
    if (selectedCategory === "all") return true;

    // Handle base categories
    if (selectedCategory === "video") {
      return section.mediaType === "video";
    }
    if (selectedCategory === "sound") {
      return section.mediaType === "audio";
    }

    // Handle custom categories
    const sectionLabel = section.categoryLabel?.trim();
    if (sectionLabel) {
      const normalizedLabel = sectionLabel.toLowerCase().replace(/\s+/g, "-");
      return normalizedLabel === selectedCategory;
    }

    return false;
  });

  // Category Button Component
  const CategoryButton = ({ category, isActive, onClick }) => {
    const Icon = category.icon;
    return (
      <motion.button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
          isActive
            ? "text-white shadow-lg"
            : "text-white/70 hover:text-white border border-white/10"
        }`}
        style={{
          backgroundColor: isActive
            ? "var(--primary-color)"
            : "rgba(255, 255, 255, 0.05)",
          borderColor: isActive
            ? "var(--primary-color)"
            : "rgba(255, 255, 255, 0.1)",
          boxShadow: isActive
            ? "0 4px 15px rgba(var(--primary-color-rgb), 0.3)"
            : "none",
        }}
        whileHover={{
          scale: 1.05,
          backgroundColor: isActive
            ? "var(--primary-color)"
            : "rgba(255, 255, 255, 0.1)",
          borderColor: isActive
            ? "var(--primary-color)"
            : "var(--primary-color)",
        }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Icon
          size={16}
          style={{ color: isActive ? "white" : "var(--primary-color)" }}
        />
        {category.label}
      </motion.button>
    );
  };

  // Debug logging to verify order preservation
  console.log(
    "[DynamicDeliverySection] Rendering sections in order:",
    sections.map((section, index) => ({
      index,
      title: section.title || `Section ${index + 1}`,
      id: section.id || `section-${index}`,
    }))
  );

  // Handle Google Drive redirect
  const handleOpenDriveFolder = () => {
    if (deliveryDriveUrl) {
      window.open(deliveryDriveUrl, '_blank');
    }
  };

  if (!sections.length) {
    return (
      <section className="min-h-screen bg-black text-white pt-[25vh]">
        <h2
          className="text-5xl md:text-7xl font-bold text-center mb-8 text-white"
          style={{ textShadow: "0 2px 10px rgba(0, 0, 0, 0.6)" }}
        >
          Delivery
        </h2>
        <div className="max-w-6xl mx-auto px-6 bg-white/5 rounded-xl p-10 text-center">
          <h3 className="text-2xl font-medium mb-4">No delivery content yet</h3>
          <p className="text-white/70 mb-4">
            There are no delivery sections in this profile. Please add content
            in the admin editor.
          </p>
        </div>
      </section>
    );
  }


  return (
    <section
      className="min-h-screen relative flex items-center justify-center py-20 overflow-hidden -mt-[20vh] z-10"
      style={{
        backgroundColor: "rgba(17, 17, 17, 0.5)",
        color: "rgba(255, 255, 255, 0.7)",
      }}
    >
      {/* Gradient overlay */}
      <div
        className="absolute inset-x-0 top-0 h-[30vh] pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(17, 17, 17, 0) 0%, rgba(17, 17, 17, 0.3) 40%, rgba(17, 17, 17, 0.5) 100%)",
          zIndex: -5,
        }}
      />
      {/* Dot pattern background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/20 opacity-50"></div>
      </div>
      <div className="relative z-10 w-full mx-auto pt-[25vh]">
        {/* Title */}
        <div className="max-w-7xl mx-auto px-6 mb-20">
          <h2 className="text-5xl md:text-7xl mb-8 font-bold text-white">
            Delivery
          </h2>
        </div>

        {/* Google Drive Folder Button */}
        {deliveryDriveUrl ? (
          <div className="flex flex-col items-center mb-10">
            <button
              onClick={handleOpenDriveFolder}
              className="px-6 py-3 rounded-lg bg-[var(--primary-color)] text-white font-semibold shadow hover:bg-[var(--primary-color-dark)] transition-colors flex items-center gap-2"
            >
              <ExternalLink size={18} />
              Open Delivery Folder
            </button>
            <div className="text-xs text-white/60 mt-2">
              Click to open the delivery folder in Google Drive
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center mb-10">
            <div className="text-white/60 text-sm text-center">
              Delivery folder not configured. Please set the Google Drive URL in the editor.
            </div>
          </div>
        )}

        {/* Category Filter Tabs */}
        {sections.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 mb-12">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-white/70">
                <Filter size={16} />
                <span className="text-sm font-medium">Filter by category:</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {allCategories.map((category) => (
                  <CategoryButton
                    key={category.id}
                    category={category}
                    isActive={selectedCategory === category.id}
                    onClick={() => setSelectedCategory(category.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sections */}
        {filteredSections.length === 0 ? (
          <div className="max-w-7xl mx-auto px-6 mb-16 text-center">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-12 border border-white/10">
              <div className="text-white/60 text-lg">
                No content found for the selected category.
              </div>
              <button
                onClick={() => setSelectedCategory("all")}
                className="mt-4 px-6 py-2 text-white rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  backgroundColor: "var(--primary-color)",
                  boxShadow: "0 4px 15px rgba(var(--primary-color-rgb), 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "var(--secondary-color)";
                  e.target.style.boxShadow =
                    "0 4px 15px rgba(var(--secondary-color-rgb), 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "var(--primary-color)";
                  e.target.style.boxShadow =
                    "0 4px 15px rgba(var(--primary-color-rgb), 0.3)";
                }}
              >
                Show All Content
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`grid gap-8 max-w-7xl mx-auto px-6 mb-16 ${
              filteredSections.length === 1
                ? "grid-cols-1 max-w-4xl"
                : filteredSections.length === 2
                ? "grid-cols-1 lg:grid-cols-2"
                : filteredSections.length === 3
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            <AnimatePresence mode="wait">
              {filteredSections.map((section, sectionIndex) => (
                <motion.div
                  key={`${section.title || sectionIndex}-${selectedCategory}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3, delay: sectionIndex * 0.1 }}
                  className="backdrop-blur-sm rounded-2xl p-8 shadow-lg transition-all duration-300 hover:shadow-xl"
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderColor: "rgba(255, 255, 255, 0.1)",
                    borderWidth: "1px",
                    borderStyle: "solid",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--primary-color)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 25px rgba(var(--primary-color-rgb), 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(255, 255, 255, 0.1)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(0, 0, 0, 0.2)";
                  }}
                >
                  {/* Section Title */}
                  <div className="flex items-center justify-between mb-10">
                    <h3 className="text-3xl md:text-4xl font-bold text-white">
                      {section.title || `Delivery ${sectionIndex + 1}`}
                    </h3>
                    <div className="w-14 h-14 flex items-center justify-center transition-all duration-300">
                      {section.mediaType === "video" ? (
                        <Tv
                          size={32}
                          style={{ color: "var(--accent-color)" }}
                        />
                      ) : (
                        <Volume2
                          size={32}
                          style={{ color: "var(--secondary-color)" }}
                        />
                      )}
                    </div>
                  </div>
                  {/* Media */}
                  {section.mediaUrl && (
                    <div className="w-full mx-auto mb-16 max-w-5xl">
                      {section.mediaType === "video" ? (
                        <div className="relative overflow-hidden rounded-2xl bg-black/60 border border-white/10 shadow-lg group">
                          {/* Idle state – show poster & play button */}
                          {!playingVideos[sectionIndex] && (
                            <div className="relative">
                              {section.imageUrl ? (
                                <img
                                  src={section.imageUrl}
                                  alt={section.title || "Video poster"}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-0 pt-[56.25%] bg-black" />
                              )}

                              <button
                                type="button"
                                onClick={() => handleVideoToggle(sectionIndex)}
                                className="absolute inset-0 flex items-center justify-center text-white/90 focus:outline-none"
                                aria-label="Play video"
                              >
                                <div
                                  className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg opacity-90 transform group-hover:scale-110 transition-transform duration-200"
                                  style={{
                                    backgroundColor: "var(--accent-color)",
                                    boxShadow:
                                      "0 4px 15px rgba(var(--accent-color-rgb), 0.4)",
                                  }}
                                >
                                  <Play
                                    size={20}
                                    className="text-white ml-0.5"
                                    fill="currentColor"
                                  />
                                </div>
                              </button>

                              {section.title && (
                                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                                  <h3 className="text-lg font-semibold text-white truncate">
                                    {section.title}
                                  </h3>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Playing state – show the actual video */}
                          {playingVideos[sectionIndex] && (
                            <video
                              src={section.mediaUrl}
                              autoPlay
                              controls
                              playsInline
                              className="w-full h-full object-contain bg-black"
                              onEnded={() => handleVideoToggle(sectionIndex)}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="w-full p-8 rounded-2xl bg-gradient-to-br from-black/40 to-black/20 shadow-lg border border-white/10">
                          <WaveformPlayer
                            key={`delivery-waveform-${sectionIndex}-${section.mediaUrl}`}
                            url={section.mediaUrl}
                            title={section.title || "Audio Track"}
                            subtitle={section.description || ""}
                            artwork={section.imageUrl || null}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {/* Paragraphs */}
                  {section.paragraphs?.length > 0 && (
                    <div className="w-full mx-auto max-w-5xl">
                      <div className="space-y-8">
                        {section.paragraphs
                          .filter((p) => p.trim())
                          .map((paragraph, idx) => {
                            // Check for optimized content
                            const optKey = `deliverySections.${sectionIndex}.paragraphs.${idx}`;
                            const optimized = optimizedContent[optKey];
                            return (
                              <div
                                key={idx}
                                className="flex items-start gap-5 p-4 rounded-xl transition-all duration-300 border border-transparent group"
                                style={{
                                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                                  borderColor: "transparent",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor =
                                    "var(--primary-color)";
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(var(--primary-color-rgb), 0.05)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor =
                                    "transparent";
                                  e.currentTarget.style.backgroundColor =
                                    "rgba(0, 0, 0, 0.1)";
                                }}
                              >
                                <div className="flex-shrink-0 mt-1.5">
                                  <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
                                    style={{
                                      backgroundColor:
                                        "rgba(var(--primary-color-rgb), 0.1)",
                                      border: "1px solid var(--primary-color)",
                                      boxShadow:
                                        "0 2px 10px rgba(var(--primary-color-rgb), 0.2)",
                                    }}
                                  >
                                    <span
                                      style={{ color: "var(--primary-color)" }}
                                    >
                                      {
                                        paragraphIcons[
                                          idx % paragraphIcons.length
                                        ]
                                      }
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <p
                                    className="text-xl md:text-2xl font-medium leading-relaxed text-white/90 group-hover:text-white transition-colors duration-300 whitespace-pre-wrap"
                                    style={{ letterSpacing: "0.01em" }}
                                  >
                                    {optimized?.displayContent || paragraph}
                                  </p>
                                  <div
                                    className="w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-[var(--primary-color)] to-transparent mt-2 transition-all duration-500 opacity-0 group-hover:opacity-100"
                                    style={{ transitionDelay: "0.1s" }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  {/* Empty state for section */}
                  {!section.mediaUrl &&
                    (!section.paragraphs || !section.paragraphs.length) && (
                      <div className="text-gray-500 italic">
                        No content for this section.
                      </div>
                    )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
};

export default DynamicDeliverySection;
